import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, BookOpen, Search, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { useRecipes, useItems, useUpsertRecipe } from '../hooks/useInventory';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../utils/api';

// ── Types ──────────────────────────────────────────────────────────────────────
interface IngredientRow {
  itemId: string;
  qty: number;
  unit: string;
  notes: string;
}

interface PosProduct {
  id: string | number;
  name: string;
  price: number;
  category?: { name?: string } | string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const UNITS = ['g', 'kg', 'ml', 'litros', 'unidades', 'porciones', 'cucharadas', 'tazas'];

function IngredientRowForm({
  row, index, items, onChange, onRemove,
}: {
  row: IngredientRow;
  index: number;
  items: { id: string; name: string; unit: string; cost?: number }[];
  onChange: (index: number, field: keyof IngredientRow, value: string | number) => void;
  onRemove: (index: number) => void;
}) {
  const selectedItem = items.find((i) => String(i.id) === String(row.itemId));
  const subtotal = selectedItem ? row.qty * (selectedItem.cost ?? 0) : 0;

  return (
    <div className="grid grid-cols-12 gap-2 items-center py-2 border-b border-divider/40 last:border-0">
      <div className="col-span-4">
        <select
          value={row.itemId}
          onChange={(e) => onChange(index, 'itemId', e.target.value)}
          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Ingrediente"
        >
          <option value="">-- Ingrediente --</option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
          ))}
        </select>
      </div>
      <div className="col-span-2">
        <input
          type="number"
          min={0}
          step="any"
          value={row.qty}
          onChange={(e) => onChange(index, 'qty', parseFloat(e.target.value) || 0)}
          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Cant."
          aria-label="Cantidad"
        />
      </div>
      <div className="col-span-2">
        <select
          value={row.unit}
          onChange={(e) => onChange(index, 'unit', e.target.value)}
          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Unidad"
        >
          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div className="col-span-2 text-right pr-2">
        <span className="text-[10px] block text-muted leading-none">Costo</span>
        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
          ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </span>
      </div>
      <div className="col-span-1">
        <input
          type="text"
          value={row.notes}
          onChange={(e) => onChange(index, 'notes', e.target.value)}
          placeholder="Notas"
          className="w-full rounded-xl border border-divider bg-surface-muted px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Notas"
        />
      </div>
      <div className="col-span-1 flex justify-center">
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 hover:bg-red-50 transition"
          aria-label="Eliminar ingrediente"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
const RecipesPage: React.FC = () => {
  const { data: recipes, isLoading: loadingRecipes } = useRecipes();
  const { data: itemsPage } = useItems({ limit: 200 });
  const upsert = useUpsertRecipe();

  // Load POS products from the main API
  const { data: posProducts } = useQuery<PosProduct[]>({
    queryKey: ['products', 'forRecipes'],
    queryFn: async () => {
      const res = await api.get('/products');
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    staleTime: 60_000,
  });

  const inventoryItems = (itemsPage?.data ?? []).map((i) => ({
    id: i.id,
    name: i.name,
    unit: i.unit ?? '',
    cost: i.cost ?? 0,
  }));

  // ── Local state ────────────────────────────────────────────────────────────
  const [selectedProductId, setSelectedProductId] = useState('');
  const [recipeName, setRecipeName] = useState('');
  const [ingredients, setIngredients] = useState<IngredientRow[]>([
    { itemId: '', qty: 1, unit: 'g', notes: '' },
  ]);
  const [q, setQ] = useState('');
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);

  const selectedProduct = (posProducts ?? []).find((p) => String(p.id) === String(selectedProductId));
  const productPrice = selectedProduct?.price ?? 0;

  const currentRecipeCost = useMemo(() => {
    let total = 0;
    ingredients.forEach((row) => {
      if (!row.itemId) return;
      const item = inventoryItems.find((i) => String(i.id) === String(row.itemId));
      if (item) {
        total += row.qty * (item.cost ?? 0);
      }
    });
    return total;
  }, [ingredients, inventoryItems]);

  const foodCostPct = productPrice > 0 ? (currentRecipeCost / productPrice) * 100 : 0;
  const marginNet = productPrice - currentRecipeCost;

  const filteredRecipes = (recipes ?? []).filter((r) =>
    r.name?.toLowerCase().includes(q.toLowerCase()) ||
    String(r.productId).toLowerCase().includes(q.toLowerCase())
  );

  const handleIngredientChange = (index: number, field: keyof IngredientRow, value: string | number) => {
    setIngredients((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addIngredient = () => {
    setIngredients((prev) => [...prev, { itemId: '', qty: 1, unit: 'g', notes: '' }]);
  };

  const removeIngredient = (index: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!selectedProductId) { toast.error('Selecciona un producto'); return; }
    const valid = ingredients.filter((i) => i.itemId && i.qty > 0);
    if (valid.length === 0) { toast.error('Agrega al menos un ingrediente válido'); return; }

    upsert.mutate(
      {
        productId: String(selectedProductId),
        name: recipeName || undefined,
        ingredients: valid.map((i) => ({
          itemId: i.itemId,
          qty: i.qty,
          unit: i.unit,
          notes: i.notes || undefined,
        })),
      },
      {
        onSuccess: () => {
          toast.success('Receta guardada correctamente');
          setSelectedProductId('');
          setRecipeName('');
          setIngredients([{ itemId: '', qty: 1, unit: 'g', notes: '' }]);
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message ?? 'Error al guardar la receta');
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-8">

        {/* Header */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted">Inventario</p>
          <h1 className="text-3xl font-bold text-[color:var(--color-text)] flex items-center gap-3">
            <BookOpen size={28} className="text-primary" /> Recetas de Productos
          </h1>
          <p className="mt-1 text-sm text-muted">
            Define los ingredientes de cada producto del POS para que el stock se descuente automáticamente al cobrar.
          </p>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-divider bg-surface p-6 shadow-sm space-y-5">
          <h2 className="text-base font-bold text-[color:var(--color-text)]">Nueva receta / Actualizar</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="product-select">
                Producto del POS
              </label>
              <select
                id="product-select"
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">-- Selecciona un producto --</option>
                {(posProducts ?? []).map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name} (${Number(p.price).toLocaleString()}) {typeof p.category === 'object' ? `(${p.category?.name})` : p.category ? `(${p.category})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="recipe-name">
                Nombre de la receta (opcional)
              </label>
              <input
                id="recipe-name"
                type="text"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder="Ej: Banana Split estándar"
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 px-1 text-[10px] font-bold uppercase text-muted">
            <div className="col-span-4">Ingrediente</div>
            <div className="col-span-2">Cantidad</div>
            <div className="col-span-2">Unidad</div>
            <div className="col-span-3">Notas</div>
            <div className="col-span-1" />
          </div>

          <div>
            {ingredients.map((row, i) => (
              <IngredientRowForm
                key={i}
                row={row}
                index={i}
                items={inventoryItems}
                onChange={handleIngredientChange}
                onRemove={removeIngredient}
              />
            ))}
          </div>

          {selectedProductId && (
            <div className="mt-4 rounded-xl border border-divider p-4 bg-surface-muted grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-[10px] font-bold uppercase text-muted mb-0.5">Precio de Venta (POS)</p>
                <p className="text-lg font-bold text-[color:var(--color-text)]">
                  ${productPrice.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase text-muted mb-0.5">Costo de Producción</p>
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  ${currentRecipeCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase text-muted mb-0.5">Margen Neto</p>
                <p className={`text-lg font-bold ${marginNet >= 0 ? 'text-primary' : 'text-red-500'}`}>
                  ${marginNet.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase text-muted mb-0.5">Costo de Alimento (Food Cost)</p>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-[color:var(--color-text)]">
                    {foodCostPct.toFixed(1)}%
                  </span>
                  <span className={`h-2.5 w-2.5 rounded-full ${
                    foodCostPct <= 30 ? 'bg-emerald-500' : foodCostPct <= 45 ? 'bg-amber-400' : 'bg-red-500'
                  }`} title={foodCostPct <= 30 ? 'Excelente' : foodCostPct <= 45 ? 'Moderado' : 'Alto'} />
                </div>
              </div>
            </div>
          )}

          {/* Sub-recipe Helper Notice */}
          <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 text-xs text-blue-700 dark:text-blue-300">
            <h4 className="font-semibold mb-1">💡 Consejos para Sub-recetas y Elaborados</h4>
            <p>
              Para usar una preparación elaborada (como la <strong>Crema de leche</strong>) dentro de la receta de otro producto (como la <strong>Ensalada de frutas</strong>):
            </p>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>Registra la Crema de Leche como un insumo en el inventario con unidad "g" o "ml".</li>
              <li>Asígnale un costo unitario estimado basándote en el Costo de Producción de su propia receta.</li>
              <li>Usa ese insumo ("Crema de leche") directamente en la receta de tus ensaladas.</li>
            </ul>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={addIngredient}
              className="inline-flex items-center gap-2 rounded-full border border-divider px-4 py-2 text-sm font-semibold transition hover:bg-surface-muted"
            >
              <Plus size={14} /> Agregar ingrediente
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={upsert.isPending}
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2 text-sm font-bold text-white shadow transition hover:opacity-90 disabled:opacity-60"
            >
              <Save size={14} />
              {upsert.isPending ? 'Guardando...' : 'Guardar receta'}
            </button>
          </div>
        </div>

        {/* Existing recipes list */}
        <div className="rounded-2xl border border-divider bg-surface p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-[color:var(--color-text)]">Recetas existentes</h2>
            <div className="relative ml-auto w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Buscar..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-full border border-divider bg-surface-muted pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {loadingRecipes ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-surface-muted" />
            ))}</div>
          ) : filteredRecipes.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">
              No hay recetas creadas aún. ¡Crea la primera!
            </p>
          ) : (
            <div className="space-y-2">
              {filteredRecipes.map((recipe) => {
                const isOpen = expandedRecipe === recipe._id;
                const product = (posProducts ?? []).find((p) => String(p.id) === String(recipe.productId));
                
                // Calculate total cost of this existing recipe
                const totalCost = recipe.ingredients.reduce((acc, ing) => {
                  const cost = (ing.item as any)?.cost ?? 0;
                  return acc + (ing.qty * cost);
                }, 0);

                return (
                  <div key={recipe._id} className="rounded-2xl border border-divider overflow-hidden">
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted transition"
                      onClick={() => setExpandedRecipe(isOpen ? null : recipe._id)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-[color:var(--color-text)]">
                          {recipe.name ?? product?.name ?? recipe.productId}
                        </p>
                        <p className="text-xs text-muted">
                          {recipe.ingredients.length} ingrediente(s) · Costo de prod.: <span className="font-bold text-emerald-600 dark:text-emerald-400">${totalCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
                        </p>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-primary font-semibold mr-2 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProductId(recipe.productId);
                          setRecipeName(recipe.name ?? '');
                          setIngredients(recipe.ingredients.map((ing) => ({
                            itemId: (ing.item as any)?.id ?? (ing.item as any)?._id ?? String(ing.item),
                            qty: ing.qty,
                            unit: ing.unit,
                            notes: ing.notes ?? '',
                          })));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Editar
                      </button>
                      {isOpen ? <ChevronUp size={16} className="text-muted" /> : <ChevronDown size={16} className="text-muted" />}
                    </button>
                    {isOpen && (
                      <div className="border-t border-divider bg-surface-muted px-4 py-3">
                        <div className="space-y-1">
                          {recipe.ingredients.map((ing, i) => {
                            const cost = (ing.item as any)?.cost ?? 0;
                            const ingCost = ing.qty * cost;
                            return (
                              <div key={i} className="flex items-center justify-between text-sm">
                                <span className="font-medium text-[color:var(--color-text)]">
                                  {(ing.item as any)?.name ?? 'Ingrediente'}
                                </span>
                                <span className="text-muted">
                                  {ing.qty} {ing.unit} {cost > 0 && `(Subt: $${ingCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`}
                                  {ing.notes ? ` · ${ing.notes}` : ''}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipesPage;
